import clsx from "clsx";
import { type FC, useMemo, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { responsive } from "utils/theme";
import { useLocale } from "hooks";
import type { Swiper as ISwiper } from "swiper";

interface IProps {
  tabs: Array<{ tabName: string; id: string | number; slug: string }>;
  activeTab: string | number;
  onTabChange: Function;
  smallFont?: boolean;
}

const TabRenderer: FC<IProps> = ({ tabs, activeTab, onTabChange }) => {
  const { isRTL, locale } = useLocale();
  const [swiperInstance, setSwiperInstance] = useState<ISwiper | null>(null);
  const handlePreviousSlideClick = () => {
    swiperInstance?.slidePrev();
  };

  const handleNextSlideClick = () => {
    swiperInstance?.slideNext();
  };

  const renderTabs = useMemo(() => {
    return (
      <div className="tw-relative">
        <div
          className={clsx("tw-absolute tw-top-0 md:tw-hidden", {
            "tw-right-0": isRTL,
            "tw-left-[-20px]": !isRTL,
          })}
        >
          <button
            type="button"
            onClick={handlePreviousSlideClick}
            className={clsx(
              "tw-w-full tw-cursor-pointer tw-text-[#344456] tw-text-base xxxl:tw-text-[21.33px] tw-font-bold tw-px-3 tw-ml-4 tw-py-2.5 tw-bg-zero tw-border-0 tw-min-w-fit"
            )}
          >
            <svg
              style={{ rotate: `${isRTL ? 0 : 180}deg` }}
              width="7"
              height="11"
              viewBox="0 0 7 11"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M0.158333 9.61667L3.975 5.79167L0.158333 1.96667L1.33333 0.791667L6.33333 5.79167L1.33333 10.7917"
                fill="#212B36"
              />
            </svg>
          </button>
        </div>
        <div
          className={clsx("tw-absolute tw-top-0 md:tw-hidden", {
            "tw-right-0": !isRTL,
            "tw-left-0": isRTL,
          })}
        >
          <button
            type="button"
            onClick={handleNextSlideClick}
            className={clsx(
              "tw-w-full tw-cursor-pointer tw-text-[#344456] tw-text-base xxxl:tw-text-[21.33px] tw-font-bold tw-px-3 tw-ml-4 tw-py-2.5 tw-bg-zero tw-border-0 tw-min-w-fit"
            )}
          >
            <svg
              style={{ rotate: `${isRTL ? 0 : 180}deg` }}
              width="7"
              height="11"
              viewBox="0 0 7 11"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M6.84167 9.40833L3.025 5.58333L6.84167 1.75833L5.66667 0.583333L0.666667 5.58333L5.66667 10.5833"
                fill="#212B36"
              />
            </svg>
          </button>
        </div>
        <Swiper
          key={locale}
          className={"tw-text-center tw-w-4/5 md:tw-w-full"}
          onSwiper={setSwiperInstance}
          dir={isRTL ? "rtl" : "ltr"}
          spaceBetween={20}
          centeredSlides={false}
          slidesPerView="auto"
          touchRatio={0}
          breakpoints={{
            "@0.00": {
              touchRatio: 1,
            },
            [responsive.lg]: {
              touchRatio: 0,
            },
          }}
        >
          {tabs?.map?.((item) => (
            <SwiperSlide
              className="tw-w-auto tw-whitespace-nowrap"
              key={item?.slug}
              id={`tab-${item?.id}`}
            >
              <div
                onClick={() => onTabChange(item)}
                className={clsx(
                  "tw-cursor-pointer tw-text-[#344456] tw-text-sm lg:tw-text-base tw-px-1 xxxl:tw-px-[13.33px] tw-font-bold tw-py-2.5 tw-bg-zero tw-border-0 tw-border-solid tw-border-b-4 ",
                  {
                    "tw-border-zero hover:tw-text-[#007FAC]":
                      activeTab?.slug !== item?.slug,
                    "tw-border-[#007FAC]": activeTab?.slug === item?.slug,
                  }
                )}
              >
                {item?.tabName}
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    );
  }, [tabs, activeTab]);
  return renderTabs;
};

export default TabRenderer;
